import React, {useEffect, useMemo, useState} from 'react';
import {SideBarStates} from '../../../context/SidebarContext';
import {BasePanelComponent} from './CommonComponents';
import {useRouter} from 'next/router';
import {useInvalidateQueries} from '@hooks/useInvalidateQueries';
import {Notification, NotificationsQuery, useMarkReadAllNotificationsMutation, useNotificationsQuery, useToggleReadNotificationMutation,} from '@graphql/generated-graphql';
import {getClient} from '@graphql/graphql-client/graphClient';
import {SimpleSpinner} from '@components/common/loading/SimpleSpinner';
import {Toggle} from '@components/base';
import {WithTooltip} from '@components/common/tooltip/SkipperTooltip';
import {QuizIconSVG} from '@components/common/svgs/QuizIconSVG';
import {AssignmentPaperSVG} from '@components/common/svgs/AssignmentPaperSVG';
import {prettyDateOrTime} from '@utils/dateFormatter';
import {useQueryClient} from 'react-query';
import {CourseRibbonBackgroundSVGNew} from '@components/common/svgs/CourseRibbonBackgroundSVG';
import {CommentIconSVG} from "@components/common/svgs/CommentIconSVG";
import {Tab} from "@headlessui/react";
import {TealTab} from "@components/base/elements/Tabs";
import {NotificationGroups} from "@components/types";
import {AnimatedSearchField} from "@components/form/animated/AnimatedSearchField";
import {useDebounce} from "@hooks/useDebounce";
import dayjs from "dayjs";
import {GradebookAnnotationPostedSVG} from "@components/common/svgs/GradebookAttachmentSVG";
import {LateStatus, ReassignStatus} from "@components/common/svgs/StatusIconSVGs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';
import {useUserSession} from "@hooks/user/useDefaultSession";

export function NotificationPanel() {
    const router = useRouter();
    const invalidateQueries = useInvalidateQueries();
    const [currentUser] = useUserSession();
    const {data, isLoading} = useNotificationsQuery(getClient('notifications'));
    const [onlyUnreadMap, setOnlyUnreadMap] = useState<Record<number, boolean>>({
        0: false,
        1: false,
    });
    const [tabIndex, setTabIndex] = useState(0);
    const onlyUnread = onlyUnreadMap[tabIndex];

    const [searchTerm, setSearchTerm] = useState<string>('');
    const debounceSearchKeyword = useDebounce(
        searchTerm.replace(/\s+/g, '').trim().toLowerCase(),
        500,
    );
    const markReadAll = useMarkReadAllNotificationsMutation(getClient('notifications'), {
        onSuccess: () => invalidateQueries(['Notifications']),
    });

    const groupedNotifications = useMemo(() => {
        return {
            direct: data?.notifications.filter(n => NotificationGroups.direct.includes(n.notificationType as any)) || [],
            watching: data?.notifications.filter(n => NotificationGroups.watching.includes(n.notificationType as any)) || [],
        }
    }, [data]);

    const filteredNotifications = useMemo(() => {
        return (tabIndex == 0 ? groupedNotifications.direct : groupedNotifications.watching)
            .filter(n => [n.title, n.message, n.targetUser.fullName].join(' ').toLowerCase().includes(debounceSearchKeyword))
            .filter((n) => !onlyUnread || !n.isViewed)
    }, [data, onlyUnread, groupedNotifications, tabIndex, debounceSearchKeyword]);

    useEffect(() => {
        setTabIndex(0)
    }, [currentUser?.id]);

    return (
        <BasePanelComponent openState={SideBarStates.Notifications}>
            <BasePanelComponent.BorderlessHeader
                title="Notifications"
                content={
                    <div className="flex">
                        <AnimatedSearchField
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                            }}
                            onClose={() => {
                                setSearchTerm('');
                            }}
                            size="small"
                            placeholder="Search"
                        />
                    </div>
                }></BasePanelComponent.BorderlessHeader>
            <BasePanelComponent.Body>
                <Tab.Group onChange={setTabIndex} selectedIndex={tabIndex} as={React.Fragment}>
                    <Tab.List className="border-b border-gray-200 flex mx-4 md:mx-[24px]">
                        <Tab className="focus:outline-none">
                            {({selected}) => (
                                <TealTab selected={selected} text={'Direct'}>
                                    <UnreadNotificationsBadge items={groupedNotifications.direct as any}/>
                                </TealTab>
                            )}
                        </Tab>
                        {groupedNotifications.watching?.length > 0 && (
                            <Tab className="focus:outline-none">
                                {({selected}) => (
                                    <TealTab selected={selected} text={'Watching'}>
                                        <UnreadNotificationsBadge items={groupedNotifications.watching as any}/>
                                    </TealTab>
                                )}
                            </Tab>
                        )}
                    </Tab.List>
                </Tab.Group>

                <div className="flex flex-col overflow-hidden flex-grow h-full">
                    {isLoading ? (
                        <SimpleSpinner className={'mx-auto my-8'}/>
                    ) : (
                        <>
                            <div className={`flex items-center gap-3 my-3 w-full px-4`}>
                                <Toggle
                                    enabled={onlyUnread}
                                    setEnabled={(enabled) =>
                                        setOnlyUnreadMap((prev) => ({
                                            ...prev,
                                            [tabIndex]: enabled,
                                        }))
                                    }
                                />
                                <span className={`text-sm`}>Only show unread</span>
                                <button
                                    className={`text-teal-600 text-sm grow text-right`}
                                    onClick={() => markReadAll.mutate({
                                        types: (tabIndex == 0 ? NotificationGroups.direct : NotificationGroups.watching) as any,
                                    })}
                                >
                                    Mark all as read
                                </button>
                            </div>
                            <div className="flex flex-col m-2 gap-2 overflow-auto">
                                {(filteredNotifications?.length || 0) === 0 ? (
                                    <div className="text-center text-gray-300 my-8">
                                        No notifications
                                    </div>
                                ) : (
                                    filteredNotifications.map((n, i) => (
                                        <NotificationItem key={i} notification={n as any}/>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
                </BasePanelComponent.Body>
        </BasePanelComponent>
    );
}

function UnreadNotificationsBadge({items}: { items: Notification[] }) {
    const unreadItems = items.filter((n) => !n.isViewed);
    if (unreadItems.length > 0)
        return (
            <WithTooltip content={`New notifications`}>
                <span
                    className={`w-5 h-5 rounded-full top-[-0.5rem] right-[-0.5rem] text-xs text-center justify-center flex items-center bg-teal-200 text-teal-600`}>
                    {unreadItems.length}
                </span>
            </WithTooltip>
        );
    else return <></>;
}

function NotificationItem({notification}: { notification: Notification }) {
    const router = useRouter();
    const queryClient = useQueryClient();
    const toggleRead = useToggleReadNotificationMutation(getClient('notifications'), {
        onSuccess: (data, args) => {
            // inline update query data for performance
            const currentData = queryClient.getQueryData('Notifications') as NotificationsQuery;
            currentData.notifications.forEach((item) => {
                if (item.id == args.id) item.isViewed = !item.isViewed;
            });
            queryClient.setQueryData('Notifications', {...currentData});
        },
    });
    const handleToggleRead = (e) => {
        e.preventDefault();
        toggleRead.mutate({id: notification.id});
    };
    const handleClick = (e) => {
        if (!(e.target as HTMLElement).closest('button') && notification.url) {
            if (notification.url.startsWith('?')) {
                // query URL - requires query params interpolation
                const newQueryParams = new URLSearchParams(notification.url);
                router.push({
                    pathname: router.pathname,
                    query: {
                        ...router.query,
                        ...Object.fromEntries(newQueryParams.entries()),
                    },
                });
            }
            else
                router.push(notification.url)
            if (!notification.isViewed)
                toggleRead.mutate({id: notification.id})
        }
    }
    return (
        <>
            <div
                className={`flex gap-3 p-3 rounded-md ${
                    notification.url ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'
                } ${!notification.isViewed ? '' : 'bg-gray-50'}`}
                onClick={handleClick}>
                <div>
                    <WithTooltip content={notification.dotTitle}>
                        <div
                            className="rounded-full w-2.5 h-2.5 mt-1"
                            style={{backgroundColor: notification.dotColor}}
                        />
                    </WithTooltip>
                </div>
                <div className={`flex-grow text-sm flex flex-col gap-1`}>
                    <div className={`flex gap-2 justify-between items-center`}>
                        <div className={`flex gap-2 overflow-hidden text-ellipsis`}>
                            {notification.iconType == 'quiz' && <QuizIconSVG className="w-5 h-5"/>}
                            {notification.iconType == 'assignment' && <AssignmentPaperSVG className="w-5 h-5"/>}
                            {notification.iconType == 'course' && <CourseRibbonBackgroundSVGNew/>}
                            {notification.iconType == 'message' && <CommentIconSVG/>}
                            {notification.iconType == 'reassign' && <ReassignStatus/>}
                            {notification.iconType == 'annotation' && <GradebookAnnotationPostedSVG variant="green"/>}
                            {notification.iconType == 'late' && <LateStatus/>}
                            {notification.iconType == 'event' && <FontAwesomeIcon icon={faCalendarDays} className="text-gray-500 w-6 h-6" />}
                            <div>{notification.title}</div>
                        </div>
                        <span className="text-gray-400 text-xs"
                              title={dayjs(notification.createdAt).format('MMM D, YYYY h:mm A')}>
                            {prettyDateOrTime(notification.createdAt)}
                        </span>
                        <div className={'ml-auto flex gap-3 items-center'}>
                            <>
                                <button onClick={handleToggleRead} className={'group p-2 -m-2'}>
                                    <WithTooltip
                                        content={
                                            !notification.isViewed
                                                ? 'Mark as read'
                                                : 'Mark as unread'
                                        }
                                    >
                                        <div
                                            className={`w-4 h-4 rounded-full ${
                                                !notification.isViewed
                                                    ? 'bg-teal-500'
                                                    : 'bg-gray-200 border-teal-300 border-2 group-hover:bg-teal-500 group-hover:border-0'
                                            }`}
                                        />
                                    </WithTooltip>
                                </button>
                            </>
                        </div>
                    </div>
                    <div className={`text-gray-500 text-ellipsis overflow-hidden`}>
                        {notification.message}
                    </div>
                </div>
            </div>
        </>
    );
}
